<template>
    <div class="importPage">
        <section class="pageHero">
            <div>
                <div class="eyebrow">Spreadsheet Workflow</div>
                <h1>Import and Export Codes</h1>
                <p class="heroText">
                    Download the canonical XLSX template for your current account, validate a spreadsheet before import,
                    then commit the approved batch.
                </p>
            </div>
            <div class="heroMeta" v-if="user">
                <label for="targetUser" class="metaLabel">Target user</label>
                <select id="targetUser" class="form-control" v-model="selectedUserId" @change="handleTargetUserChange">
                    <option v-for="option in userOptions" :key="option.id" :value="option.id">
                        {{ option.label }}
                    </option>
                </select>
                <div class="metaValue">{{ selectedUserLabel }}</div>
                <div class="metaSubtle monoValue">{{ selectedUserId }}</div>
            </div>
        </section>

        <section class="contentGrid">
            <article class="panelCard">
                <h2>Download</h2>
                <p class="panelText">
                    Export your current codes as the fixed XLSX template used by import validation.
                </p>
                <label class="checkboxRow" for="includeDeletedInDownload">
                    <input
                        id="includeDeletedInDownload"
                        class="form-check-input"
                        type="checkbox"
                        v-model="includeDeletedInDownload"
                    />
                    <span>Include deleted codes</span>
                </label>
                <p class="panelSubtle">
                    The workbook includes a `deleted` column. Importing `deleted=true` soft-deletes that shortcode;
                    `deleted=false` or blank keeps or restores it as active.
                </p>
                <button class="btn btn-primary" type="button" :disabled="downloadBusy || !user" @click="downloadWorkbook">
                    {{ downloadBusy ? "Preparing..." : "Download XLSX" }}
                </button>
                <p v-if="downloadError" class="statusError">{{ downloadError }}</p>
            </article>

            <article class="panelCard">
                <h2>Validate Upload</h2>
                <p class="panelText">
                    Upload one `.xlsx` workbook with the `Codes` worksheet. The `Instructions` sheet is ignored.
                </p>
                <label for="importWorkbook" class="fieldLabel">Workbook</label>
                <input
                    id="importWorkbook"
                    class="form-control"
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    @change="handleFileChange"
                />
                <div v-if="selectedFileName" class="fileBadge">{{ selectedFileName }}</div>
                <button class="btn btn-primary" type="button" :disabled="validateBusy || !selectedFile" @click="validateWorkbook">
                    {{ validateBusy ? "Validating..." : "Validate Workbook" }}
                </button>
                <p v-if="validateError" class="statusError">{{ validateError }}</p>
            </article>
        </section>

        <section v-if="validation" class="resultsSection">
            <article class="panelCard panelCardWide">
                <div class="resultsHeader">
                    <div>
                        <div class="eyebrow">Validation Result</div>
                        <h2>{{ validation.ok ? "Ready to Commit" : "Validation Failed" }}</h2>
                    </div>
                    <div :class="validation.ok ? 'statusOk' : 'statusError'">
                        {{ validationStatusLabel }}
                    </div>
                </div>

                <div class="summaryGrid">
                    <div class="summaryTile">
                        <div class="summaryLabel">Rows</div>
                        <div class="summaryValue">{{ validation.rowCount }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Valid</div>
                        <div class="summaryValue">{{ validation.validRowCount }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Invalid</div>
                        <div class="summaryValue">{{ validation.invalidRowCount }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Creates</div>
                        <div class="summaryValue">{{ validation.operationCounts?.create || 0 }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Updates</div>
                        <div class="summaryValue">{{ validation.operationCounts?.update || 0 }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Reactivates</div>
                        <div class="summaryValue">{{ validation.operationCounts?.reactivate || 0 }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Deletes</div>
                        <div class="summaryValue">{{ validation.operationCounts?.delete || 0 }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Delete No-op</div>
                        <div class="summaryValue">{{ validation.operationCounts?.delete_noop || 0 }}</div>
                    </div>
                </div>

                <p class="batchMeta">
                    Batch ID:
                    <span class="monoValue">{{ validation.batchId || "Unavailable" }}</span>
                </p>
                <p class="batchMeta">
                    Target user:
                    <span class="monoValue">{{ validation.targetUser?.id || selectedUserId }}</span>
                </p>

                <div class="reportBar">
                    <button class="btn btn-outline-primary" type="button" @click="downloadValidationReport">
                        Download Validation Report
                    </button>
                </div>

                <div v-if="validationWarnings.length > 0" class="tableWrap">
                    <table class="table resultTable">
                        <thead>
                            <tr>
                                <th>Warning Row</th>
                                <th>Warning</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(warning, index) in validationWarnings" :key="`${warning.code}-${index}`">
                                <td>{{ warning.excelRowNumber || "n/a" }}</td>
                                <td>{{ warning.message }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="validation.issues.length > 0" class="tableWrap">
                    <table class="table resultTable">
                        <thead>
                            <tr>
                                <th>Row</th>
                                <th>Field</th>
                                <th>Issue</th>
                                <th>Suggested UPC</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(issue, index) in validationIssues" :key="`${issue.excelRowNumber}-${issue.field}-${index}`">
                                <td>{{ issue.excelRowNumber || "n/a" }}</td>
                                <td class="monoValue">{{ issue.field }}</td>
                                <td>{{ issue.message }}</td>
                                <td class="monoValue">{{ issue.suggestedValue || "-" }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-else class="tableWrap">
                    <table class="table resultTable">
                        <thead>
                            <tr>
                                <th>Row</th>
                                <th>Action</th>
                                <th>Shortcode</th>
                                <th>Operation</th>
                                <th>Title</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in validation.rows" :key="`${row.excelRowNumber}-${row.shortcode || row.title}`">
                                <td>{{ row.excelRowNumber }}</td>
                                <td>{{ row.action }}</td>
                                <td class="monoValue">{{ row.shortcode || "new" }}</td>
                                <td>{{ row.operation }}</td>
                                <td>{{ row.title || "Delete row" }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="commitBar">
                    <button class="btn btn-primary" type="button" :disabled="!canCommit" @click="commitImport">
                        {{ commitBusy ? "Committing..." : "Commit Import" }}
                    </button>
                    <p v-if="commitError" class="statusError">{{ commitError }}</p>
                </div>
            </article>
        </section>

        <section v-if="commitResult" class="resultsSection">
            <article class="panelCard panelCardWide">
                <div class="resultsHeader">
                    <div>
                        <div class="eyebrow">Committed</div>
                        <h2>Import Applied</h2>
                    </div>
                    <div class="statusOk">Success</div>
                </div>
                <div class="summaryGrid">
                    <div class="summaryTile">
                        <div class="summaryLabel">Created</div>
                        <div class="summaryValue">{{ commitResult.created }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Updated</div>
                        <div class="summaryValue">{{ commitResult.updated }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Reactivated</div>
                        <div class="summaryValue">{{ commitResult.reactivated }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Deleted</div>
                        <div class="summaryValue">{{ commitResult.deleted }}</div>
                    </div>
                    <div class="summaryTile">
                        <div class="summaryLabel">Delete No-op</div>
                        <div class="summaryValue">{{ commitResult.deleteNoop }}</div>
                    </div>
                </div>
                <p class="batchMeta">
                    Committed at:
                    <span class="monoValue">{{ commitResult.committedAt }}</span>
                </p>
                <p class="panelText">{{ redirectingAfterCommit ? "Refreshing the code list..." : "Import complete." }}</p>
                <router-link class="btn btn-outline-primary" :to="{ path: '/codes', query: buildCodesRouteQuery(commitResult) }">
                    Return to Codes
                </router-link>
            </article>
        </section>
    </div>
</template>

<script>
import { apiFetch, apiFetchJson } from "@/libs/api";
import { getUser, getUserFullNameFromApi, normalizeUserFromApi } from "@/libs/libs";
import { getUserId } from "@/libs/auth";
import * as XLSX from "xlsx";

function downloadBlob(blob, filename) {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
}

function parseFilenameFromDisposition(headerValue) {
    const match = /filename="([^"]+)"/i.exec(headerValue || "");
    return match?.[1] || "qrexlink-codes.xlsx";
}

function extractSuggestedUpc(issue) {
    if (issue?.suggestedValue) {
        return issue.suggestedValue;
    }
    if (!issue || issue.field !== "upc" || typeof issue.message !== "string") {
        return "";
    }
    const match = issue.message.match(/\buse\s+(\d{12})\b/i);
    return match?.[1] || "";
}

function buildValidationReportWorkbook(validation) {
    const workbook = XLSX.utils.book_new();
    const warnings = Array.isArray(validation?.warnings) ? validation.warnings : [];
    const issues = Array.isArray(validation?.issues)
        ? validation.issues.map((issue) => ({ ...issue, suggestedValue: extractSuggestedUpc(issue) }))
        : [];
    const summaryRows = [
        ["reportType", "code_import_validation"],
        ["batchId", validation?.batchId || ""],
        ["templateVersion", validation?.templateVersion || ""],
        ["filename", validation?.filename || ""],
        ["worksheetName", validation?.worksheetName || ""],
        ["targetUserId", validation?.targetUser?.id || ""],
        ["targetUsername", validation?.targetUser?.username || ""],
        ["rowCount", validation?.rowCount || 0],
        ["validRowCount", validation?.validRowCount || 0],
        ["invalidRowCount", validation?.invalidRowCount || 0],
        ["ok", validation?.ok ? "true" : "false"],
        ["warningCount", warnings.length],
        ["createCount", validation?.operationCounts?.create || 0],
        ["updateCount", validation?.operationCounts?.update || 0],
        ["reactivateCount", validation?.operationCounts?.reactivate || 0],
        ["deleteCount", validation?.operationCounts?.delete || 0],
        ["deleteNoopCount", validation?.operationCounts?.delete_noop || 0],
    ];

    const detailHeader = [
        "section",
        "excelRowNumber",
        "field",
        "code",
        "message",
        "suggestedValue",
        "action",
        "shortcode",
        "operation",
        "title",
        "description",
        "redirectUrl",
        "upcEnabled",
        "upc",
    ];
    const issueRows = issues.map((issue) => [
        "issue",
        issue.excelRowNumber ?? "",
        issue.field ?? "",
        issue.code ?? "",
        issue.message ?? "",
        issue.suggestedValue ?? "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
    ]);
    const warningRows = warnings.map((warning) => [
        "warning",
        warning.excelRowNumber ?? "",
        warning.field ?? "",
        warning.code ?? "",
        warning.message ?? "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
    ]);
    const normalizedRows = (validation?.rows || []).map((row) => [
        "row",
        row.excelRowNumber ?? "",
        "",
        "",
        "",
        "",
        row.action ?? "",
        row.shortcode || "",
        row.operation || "",
        row.title || "",
        row.description || "",
        row.redirectUrl || "",
        row.upcEnabled ? "true" : "false",
        row.upc || "",
    ]);

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");
    XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet([detailHeader, ...warningRows, ...issueRows, ...normalizedRows]),
        "Results"
    );

    return XLSX.write(workbook, { type: "array", bookType: "xlsx" });
}

function buildValidationReportFilename(validation) {
    const sourceName = `${validation?.filename || "codes"}`.replace(/\.xlsx$/i, "");
    const batchId = `${validation?.batchId || "validation"}`.replace(/[^a-z0-9_-]+/gi, "-");
    return `${sourceName}-${batchId}-validation-report.xlsx`;
}

export default {
    name: "CodeImportExportView",
    display: "Import / Export",
    data() {
        return {
            user: null,
            childUsers: [],
            selectedUserId: "",
            includeDeletedInDownload: false,
            selectedFile: null,
            selectedFileName: "",
            downloadBusy: false,
            validateBusy: false,
            commitBusy: false,
            downloadError: "",
            validateError: "",
            commitError: "",
            redirectingAfterCommit: false,
            validation: null,
            commitResult: null,
        };
    },
    computed: {
        userName() {
            if (!this.user) return "";
            return getUserFullNameFromApi(this.user) || this.user.username || this.user.id;
        },
        validationWarnings() {
            return Array.isArray(this.validation?.warnings) ? this.validation.warnings : [];
        },
        validationIssues() {
            return Array.isArray(this.validation?.issues)
                ? this.validation.issues.map((issue) => ({
                    ...issue,
                    suggestedValue: extractSuggestedUpc(issue),
                }))
                : [];
        },
        validationStatusLabel() {
            const warningCount = this.validationWarnings.length;
            if (!this.validation?.ok) {
                return `${this.validation?.issues?.length || 0} issue(s)`;
            }
            if (warningCount > 0) {
                return `Clean with ${warningCount} warning(s)`;
            }
            return "Clean";
        },
        canCommit() {
            return Boolean(this.validation?.ok && this.validation?.batchId && !this.commitBusy && !this.commitResult);
        },
        userOptions() {
            const options = [];
            if (this.user) {
                options.push({
                    id: this.user.id,
                    label: `${this.userName} (${this.user.username || this.user.id})`,
                });
            }
            this.childUsers.forEach((user) => {
                options.push({
                    id: user.id,
                    label: `${getUserFullNameFromApi(user) || user.username || user.id} (${user.username || user.id})`,
                });
            });
            return options;
        },
        selectedUserLabel() {
            return this.userOptions.find((option) => option.id === this.selectedUserId)?.label || this.userName;
        },
    },
    methods: {
        resetValidationState() {
            this.validateError = "";
            this.commitError = "";
            this.validation = null;
            this.commitResult = null;
        },
        handleTargetUserChange() {
            this.resetValidationState();
        },
        handleFileChange(event) {
            const [file] = event?.target?.files || [];
            this.selectedFile = file || null;
            this.selectedFileName = file?.name || "";
            this.resetValidationState();
        },
        async loadUser() {
            const userId = getUserId();
            if (!userId) {
                this.$router.replace({ path: "/login" });
                return;
            }
            this.user = await getUser(userId);
            this.selectedUserId = this.user?.id || "";
        },
        async loadChildUsers() {
            if (!this.user?.id) return;
            try {
                const data = await apiFetchJson(`users?ownerUserId=${encodeURIComponent(this.user.id)}`, {}, { auth: true });
                const users = Array.isArray(data?.users) ? data.users.map(normalizeUserFromApi) : [];
                this.childUsers = users.filter((candidate) => candidate.id !== this.user.id);
            } catch (err) {
                console.log(`Unable to load child users for import/export ${err?.message || err}`);
                this.childUsers = [];
            }
        },
        buildCodesRouteQuery(summary = null) {
            if (!summary) return {};
            return {
                importBatchId: summary.batchId,
                importCreated: String(summary.created || 0),
                importUpdated: String(summary.updated || 0),
                importReactivated: String(summary.reactivated || 0),
                importDeleted: String(summary.deleted || 0),
                importDeleteNoop: String(summary.deleteNoop || 0),
            };
        },
        async downloadWorkbook() {
            this.downloadBusy = true;
            this.downloadError = "";
            try {
                const params = new URLSearchParams();
                if (this.selectedUserId) {
                    params.set("userid", this.selectedUserId);
                }
                if (this.includeDeletedInDownload) {
                    params.set("includeDeleted", "true");
                }
                const query = params.toString() ? `?${params.toString()}` : "";
                const res = await apiFetch(`exports/codes.xlsx${query}`, {
                    method: "GET",
                }, { auth: true });
                if (!res.ok) {
                    const message = await res.text();
                    throw new Error(message || `Export failed (${res.status})`);
                }
                const blob = await res.blob();
                const filename = parseFilenameFromDisposition(res.headers.get("content-disposition"));
                downloadBlob(blob, filename);
            } catch (err) {
                this.downloadError = err?.message || "Unable to download workbook.";
            } finally {
                this.downloadBusy = false;
            }
        },
        async validateWorkbook() {
            if (!this.selectedFile) return;
            this.validateBusy = true;
            this.validateError = "";
            this.commitError = "";
            this.commitResult = null;
            try {
                const formData = new FormData();
                formData.append("file", this.selectedFile);
                if (this.selectedUserId) {
                    formData.append("userid", this.selectedUserId);
                }
                const res = await apiFetch("imports/codes/validate", {
                    method: "POST",
                    body: formData,
                }, { auth: true });
                const contentType = res.headers.get("content-type") || "";
                const body = contentType.includes("application/json") ? await res.json() : await res.text();
                if (!res.ok) {
                    if (body && typeof body === "object") {
                        this.validation = body;
                        this.validateError = body.error || "Validation failed.";
                        return;
                    }
                    throw new Error(body || `Validation failed (${res.status})`);
                }
                this.validation = body;
            } catch (err) {
                this.validation = null;
                this.validateError = err?.message || "Unable to validate workbook.";
            } finally {
                this.validateBusy = false;
            }
        },
        downloadValidationReport() {
            if (!this.validation) return;
            const workbook = buildValidationReportWorkbook(this.validation);
            const blob = new Blob([workbook], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            downloadBlob(blob, buildValidationReportFilename(this.validation));
        },
        async commitImport() {
            if (!this.validation?.batchId) return;
            this.commitBusy = true;
            this.commitError = "";
            try {
                const body = await apiFetchJson("imports/codes/commit", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ batchId: this.validation.batchId }),
                }, { auth: true });
                this.commitResult = body;
                this.redirectingAfterCommit = true;
                window.setTimeout(() => {
                    this.$router.push({
                        path: "/codes",
                        query: this.buildCodesRouteQuery(body),
                    });
                }, 1400);
            } catch (err) {
                this.commitError = err?.message || "Unable to commit import.";
                this.redirectingAfterCommit = false;
            } finally {
                this.commitBusy = false;
            }
        },
    },
    async mounted() {
        await this.loadUser();
        await this.loadChildUsers();
    },
};
</script>

<style scoped>
.importPage {
    display: grid;
    gap: 20px;
    padding: 18px 12px 28px;
}

.pageHero {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(260px, 0.8fr);
    gap: 18px;
    align-items: start;
}

.eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 0.76rem;
    color: var(--qrce-muted);
    margin-bottom: 8px;
}

.heroText,
.panelText,
.batchMeta {
    color: var(--qrce-muted);
}

.panelSubtle {
    color: var(--qrce-muted);
    font-size: 0.95rem;
    line-height: 1.5;
    margin: -10px 0 20px;
}

.checkboxRow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 12px;
    color: var(--qrce-ink);
    font-weight: 600;
}

.heroMeta,
.panelCard {
    background: var(--qrce-surface);
    border: 1px solid var(--qrce-border);
    border-radius: var(--qrce-radius);
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
}

.heroMeta {
    padding: 18px;
    text-align: left;
}

.metaLabel,
.summaryLabel,
.fieldLabel {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.78rem;
    color: var(--qrce-muted);
}

.metaValue,
.summaryValue {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--qrce-text);
}

.metaSubtle,
.monoValue {
    font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", monospace;
}

.contentGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
}

.panelCard {
    text-align: left;
    padding: 20px;
    display: grid;
    gap: 14px;
}

.panelCardWide {
    width: 100%;
}

.fileBadge {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    max-width: 100%;
    padding: 7px 10px;
    border-radius: 999px;
    background: var(--qrce-surface-2);
    border: 1px solid var(--qrce-border);
    overflow-wrap: anywhere;
}

.resultsSection {
    display: grid;
}

.resultsHeader {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
}

.summaryGrid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}

.summaryTile {
    padding: 14px;
    border-radius: 16px;
    border: 1px solid var(--qrce-border);
    background: var(--qrce-surface-2);
}

.tableWrap {
    overflow-x: auto;
}

.resultTable {
    margin-bottom: 0;
}

.resultTable th {
    white-space: nowrap;
}

.statusError {
    color: #b91c1c;
    margin: 0;
}

.statusOk {
    color: #166534;
    font-weight: 700;
}

.commitBar {
    display: grid;
    gap: 10px;
    justify-items: start;
}

.reportBar {
    display: flex;
    justify-content: flex-start;
}

@media (max-width: 980px) {
    .pageHero,
    .contentGrid,
    .summaryGrid {
        grid-template-columns: 1fr;
    }
}
</style>
